import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Una scheda di MaggiorazioniProgetto (components/MaggiorazioniProgetto.js, 31a5bd1): i dati del progetto per la
// maggiorazione della copertura di un bando. Ogni cambio (campo, riga aggiunta o tolta) toglie la conferma: «Salva i
// dati della maggiorazione» si accende solo con la casella spuntata. Righe di soci ed esperti fino a 100.
// Salva: PUT JSON con i dati della scheda ({...blank, ...scheda.dati} con i campi della pagina); la risposta (Turbo
// Stream) porta il toast, il riquadro riletto dal server e la pagina riletta; un errore porta solo il toast e il lavoro
// resta nella pagina.
const MAX_RIGHE = 100
// Dopo un salvataggio il riquadro si rilegge dal server (refresh()): nell'originale ogni <Scheda key={grant_id}> tiene
// il suo DOM, quindi le schede aperte e le loro «Regola» restano aperte. Qui lo stato di apertura di ogni scheda
// ({testid: [scheda, ...dettagli interni]}) si annota prima di applicare la risposta e ogni scheda nuova lo riprende.
// Lo stato va a ogni scheda nuova con quel testid (la risposta puo' rendere il riquadro piu' di una volta: il riquadro
// sostituito e la pagina riletta), non a una scheda vecchia che si ricollega; vale fino al prossimo salvataggio o alla
// prossima visita (un nuovo montaggio dell'originale riparte chiuso).
const aperture = new Map()
const viste = new WeakSet()
document.addEventListener("turbo:visit", () => aperture.clear())
const tri = (v) => (v === "" ? null : v === "si")

export default class extends Controller {
  static targets = ["soci", "esperti", "socio", "esperto", "socioNuovo", "espertoNuovo", "aggiungiSocio", "aggiungiEsperto", "conferma", "salva"]
  static values = { url: String, dati: Object }

  connect() {
    this.saving = false
    const nuova = !viste.has(this.element)
    viste.add(this.element)
    const stato = nuova && aperture.get(this.element.dataset.testid)
    if (stato) {
      this.element.open = stato[0]
      this.element.querySelectorAll("details").forEach((d, i) => { if (i + 1 < stato.length) d.open = stato[i + 1] })
    }
    this.aggiorna()
  }

  annotaAperture() {
    aperture.clear()
    const frame = this.element.closest("turbo-frame") || this.element.parentElement
    frame.querySelectorAll("details[data-controller~='maggiorazioni']").forEach((scheda) => {
      aperture.set(scheda.dataset.testid, [scheda.open, ...[...scheda.querySelectorAll("details")].map((d) => d.open)])
    })
  }

  // set() dell'originale: ogni cambio dei dati toglie la conferma
  cambia(event) {
    if (this.hasConfermaTarget && event.target === this.confermaTarget) return
    this.togliConferma()
  }

  conferma() {
    this.aggiorna()
  }

  togliConferma() {
    if (this.hasConfermaTarget) this.confermaTarget.checked = false
    this.aggiorna()
  }

  // il tipo del socio: «È una donna?» e la data di nascita solo per una persona fisica
  tipo(event) {
    const riga = event.target.closest("[data-maggiorazioni-target~='socio']")
    riga.querySelector("[data-persona]").hidden = event.target.value !== "persona"
  }

  aggiungiSocio() {
    this.aggiungi(this.sociTarget, this.socioNuovoTarget)
  }

  aggiungiEsperto() {
    this.aggiungi(this.espertiTarget, this.espertoNuovoTarget)
  }

  aggiungi(elenco, modello) {
    if (elenco.children.length >= MAX_RIGHE) return
    elenco.append(modello.content.cloneNode(true))
    this.rinumera()
    this.togliConferma()
  }

  togli(event) {
    event.target.closest("[data-testid^='bonus-']").remove()
    this.rinumera()
    this.togliConferma()
  }

  rinumera() {
    ;[["socio", "Socio", this.socioTargets], ["esperto", "Esperto", this.espertoTargets]].forEach(([tipo, nome, righe]) => {
      righe.forEach((riga, i) => {
        riga.dataset.testid = `bonus-${tipo}-${i}`
        riga.querySelector("[data-numero]").textContent = `${nome} ${i + 1}`
      })
    })
  }

  aggiorna() {
    if (this.hasAggiungiSocioTarget) this.aggiungiSocioTarget.disabled = this.saving || this.socioTargets.length >= MAX_RIGHE
    if (this.hasAggiungiEspertoTarget) this.aggiungiEspertoTarget.disabled = this.saving || this.espertoTargets.length >= MAX_RIGHE
    if (this.hasConfermaTarget) this.confermaTarget.disabled = this.saving
    if (this.hasSalvaTarget) {
      this.salvaTarget.disabled = this.saving || !this.confermaTarget.checked
      this.salvaTarget.textContent = this.saving ? "Salvataggio…" : "Salva i dati della maggiorazione"
    }
    this.element.querySelectorAll("button[data-action='maggiorazioni#togli']").forEach((b) => { b.disabled = this.saving })
    // disabled = !canEdit || saving: chi modifica ha la casella di conferma; chi legge ha i campi spenti dal server
    if (this.hasConfermaTarget) this.element.querySelectorAll("input:not([type=checkbox]), select, textarea").forEach((el) => { el.disabled = this.saving })
  }

  campo(riga, nome) {
    return riga.querySelector(`[data-campo="${nome}"]`).value
  }

  dati() {
    const val = (nome) => this.element.querySelector(`[name="${nome}"]`).value
    return {
      ...this.datiValue,
      data_domanda: val("data_domanda"),
      compagine_completa: tri(val("compagine_completa")),
      soci: this.socioTargets.map((r) => ({ tipo: this.campo(r, "tipo"), donna: tri(this.campo(r, "donna")), nascita: this.campo(r, "nascita") })),
      esperti_completi: tri(val("esperti_completi")),
      esperti: this.espertoTargets.map((r) => ({ presente: tri(this.campo(r, "presente")), titolo_valido: tri(this.campo(r, "titolo_valido")),
        titolo_data: this.campo(r, "titolo_data"), stabile: tri(this.campo(r, "stabile")), estero_dal: this.campo(r, "estero_dal") })),
      evidenza: val("evidenza"),
    }
  }

  async salva() {
    const corpo = JSON.stringify(this.dati())
    this.saving = true
    this.aggiorna()
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(this.urlValue, {
        method: "PUT", credentials: "same-origin", body: corpo,
        headers: { "Content-Type": "application/json", Accept: "text/vnd.turbo-stream.html", ...(token ? { "X-CSRF-Token": token } : {}) },
      })
      if (res.redirected || !(res.headers.get("Content-Type") || "").includes("turbo-stream")) throw new Error("risposta")
      const testo = await res.text()
      if (res.ok) this.annotaAperture()
      Turbo.renderStreamMessage(testo)
    } catch {
      window.toast?.error("Si è verificato un errore. Riprova.")    // formatApiErrorDetail(undefined), sempre un testo
    } finally {
      if (this.element.isConnected) {
        this.saving = false
        this.aggiorna()
      }
    }
  }
}
